import axios from 'axios';

// Get API URL – dev uses REACT_APP_API_URL, production uses same-origin
const getApiUrl = () => {
  // Dev override: use REACT_APP_API_URL from .env if set (e.g. http://localhost:5001/api)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  // Deployed (production + preview): same-origin /api
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/api`;
  }
  // SSR/build fallback
  return 'https://trace.appetiteapps.be/api';
};

const API_URL = getApiUrl();

// Log API URL for debugging (only in browser console)
if (typeof window !== 'undefined') {
  console.log('🔧 API Configuration:', { API_URL, origin: window.location.origin });
}

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Silent refresh state – shared across all concurrent 401 responses
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const redirectToLogin = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  window.location.href = '/login';
};

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      const path = typeof window !== 'undefined' ? window.location.pathname : '';
      const isAuthPage =
        path === '/login' ||
        path === '/register' ||
        path.startsWith('/operator/login');

      // Don't attempt refresh on auth pages or on the refresh endpoint itself
      if (isAuthPage || originalRequest.url === '/auth/refresh') {
        redirectToLogin();
        return Promise.reject(error);
      }

      const refreshToken = localStorage.getItem('refreshToken');
      if (!refreshToken) {
        redirectToLogin();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Another refresh is already in flight — queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await axios.post(`${API_URL}/auth/refresh`, null, {
          headers: { Authorization: `Bearer ${refreshToken}` },
        });

        const { token: newToken, refreshToken: newRefreshToken } = response.data;
        localStorage.setItem('token', newToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        api.defaults.headers.common.Authorization = `Bearer ${newToken}`;
        processQueue(null, newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 429) {
      // Rate limit exceeded
      const retryAfter = error.response.headers['retry-after'] || '15';
      error.message = `Te veel verzoeken. Wacht ${retryAfter} minuten en probeer opnieuw.`;
      console.warn('Rate limit exceeded:', error.message);
    }

    return Promise.reject(error);
  }
);

export default api;
export { API_URL };
